/**
 * LinkRecordsEditor — the shared "Link record" control used by both the note
 * composer (`useMemoCreate`) and the note slideout (`NoteLinkedRecordsSection`).
 *
 * Renders an optional header (e.g. pre-linked entity badges), the list of
 * in-progress `LinkPickerRow`s, and the "+ Link record" button. State lives in
 * `useLinkRecordRows`; this component is purely presentational so both surfaces
 * share identical UX.
 *
 * The add button only appears once every existing row is complete (association
 * type + ≥1 target), so the user finishes one link before starting another.
 */

import { Plus } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';

import { type LinkedAssociation, LinkPickerRow } from './LinkPickerRow';

interface LinkRecordsEditorProps {
  orgId: number;
  rows: LinkedAssociation[];
  /** Association-type select items (id + label). */
  items: { id: string; label: string }[];
  /** Number of linkable association types — the add button is disabled at 0. */
  linkableCount: number;
  onAddRow: () => void;
  onRemoveRow: (id: string) => void;
  onAssociationTypeChange: (id: string, associationTypeName: string) => void;
  onTargetChange: (id: string, value: string[] | null) => void;
  isSubmitting?: boolean;
  /** Label for the add button. Defaults to "Link record". */
  addButtonLabel?: string;
  /**
   * Color variant for the add button. Defaults to the muted `link-gray`
   * (note composer). The record slideout passes `link-color` for the
   * brand-blue "+ Link record" affordance in the restyled section.
   */
  addButtonColor?: 'link-gray' | 'link-color';
  /** Optional content rendered above the rows (e.g. pre-linked badges). */
  header?: ReactNode;
}

export function LinkRecordsEditor({
  orgId,
  rows,
  items,
  linkableCount,
  onAddRow,
  onRemoveRow,
  onAssociationTypeChange,
  onTargetChange,
  isSubmitting = false,
  addButtonLabel = 'Link record',
  addButtonColor = 'link-gray',
  header,
}: LinkRecordsEditorProps) {
  // Only surface the add button once all in-progress rows are complete, so the
  // user finishes one link before starting another. An empty list is "all
  // complete", so the button shows in the initial empty state.
  const allRowsComplete = useMemo(
    () =>
      rows.every(
        l => l.associationType !== null && l.targetEntityIds.length > 0
      ),
    [rows]
  );

  return (
    <div className='flex flex-col gap-1.5'>
      {header}
      {rows.map(link => (
        <LinkPickerRow
          key={link.id}
          link={link}
          items={items}
          orgId={orgId}
          onAssociationTypeChange={onAssociationTypeChange}
          onTargetChange={onTargetChange}
          onRemove={onRemoveRow}
          isSubmitting={isSubmitting}
        />
      ))}
      {allRowsComplete && (
        <Button
          size='sm'
          color={addButtonColor}
          iconLeading={Plus}
          onClick={onAddRow}
          isDisabled={isSubmitting || linkableCount === 0}
          className='self-start'
        >
          {addButtonLabel}
        </Button>
      )}
    </div>
  );
}
